import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowDown, ArrowUp } from "lucide-react";
import { 
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataLoader } from "../components/DataLoader";
import { NewsletterSubscriber } from "../utils/types";
import { formatDate } from "../utils/formatters";
import { SearchBar } from "./members/SearchBar";
import { DateRangeFilter } from "./members/DateRangeFilter";
import { useDataFilters } from "./hooks/useDataFilters";
import { useSortData } from "./hooks/useSortData";
import { usePagination } from "./hooks/usePagination";
import { TablePagination } from "./members/TablePagination";
import { ExportButtons } from "./newsletter/ExportButtons";

export const NewsletterTab = () => {
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchNewsletterSubscribers();
  }, []);

  const fetchNewsletterSubscribers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log("Fetching newsletter subscribers...");
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .select('id, first_name, last_name, email, is_active, verified_at, created_at')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      
      console.log("Newsletter subscribers data:", data);
      setSubscribers(data || []);
    } catch (err) {
      console.error("Erreur lors de la récupération des abonnés newsletter:", err);
      setError("Impossible de récupérer les données des abonnés newsletter.");
    } finally {
      setIsLoading(false);
    }
  };

  // Filter data using the created_at field for date filtering
  const {
    startDate,
    endDate,
    searchTerm,
    filteredData,
    handleStartDateChange,
    handleEndDateChange,
    handleSearchChange
  } = useDataFilters(subscribers, 'created_at');

  // Sort data
  const { sortConfig, sortedData, sortData } = useSortData(filteredData);

  // Pagination
  const ITEMS_PER_PAGE = 20;
  const { currentPage, totalPages, paginatedData, goToPage, nextPage, prevPage } = usePagination({
    data: sortedData,
    itemsPerPage: ITEMS_PER_PAGE
  });

  // Function to display sort icon
  const getSortIcon = (columnKey: keyof NewsletterSubscriber) => {
    if (sortConfig && sortConfig.key === columnKey) {
      return sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Abonnés newsletter</CardTitle>
            <CardDescription>Liste des abonnés à la newsletter</CardDescription>
          </div>
          <ExportButtons subscribers={sortedData} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-6 space-y-4">
          <div className="w-full md:w-1/2">
            <SearchBar 
              searchTerm={searchTerm} 
              onSearchChange={handleSearchChange} 
              placeholder="Rechercher par nom, prénom ou email..." 
            />
          </div>
          
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
        </div>

        <DataLoader
          isLoading={isLoading}
          error={error}
          isEmpty={sortedData.length === 0}
          emptyMessage="Aucun abonné trouvé"
        >
          <>
            <Table>
              <TableCaption>Liste des abonnés à la newsletter</TableCaption>
              <TableHeader className="bg-primary text-white">
                <TableRow>
                  <TableHead 
                    onClick={() => sortData('first_name')}
                    className="cursor-pointer hover:bg-primary/80 transition-colors"
                  >
                    Prénom {getSortIcon('first_name')}
                  </TableHead>
                  <TableHead 
                    onClick={() => sortData('last_name')}
                    className="cursor-pointer hover:bg-primary/80 transition-colors"
                  >
                    Nom {getSortIcon('last_name')}
                  </TableHead>
                  <TableHead 
                    onClick={() => sortData('email')}
                    className="cursor-pointer hover:bg-primary/80 transition-colors"
                  >
                    Email {getSortIcon('email')}
                  </TableHead>
                  <TableHead 
                    onClick={() => sortData('is_active')}
                    className="cursor-pointer hover:bg-primary/80 transition-colors"
                  >
                    Statut {getSortIcon('is_active')}
                  </TableHead>
                  <TableHead 
                    onClick={() => sortData('verified_at')}
                    className="cursor-pointer hover:bg-primary/80 transition-colors"
                  >
                    Date de vérification {getSortIcon('verified_at')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((subscriber) => (
                  <TableRow key={subscriber.id}>
                    <TableCell>{subscriber.first_name}</TableCell>
                    <TableCell>{subscriber.last_name}</TableCell>
                    <TableCell>{subscriber.email}</TableCell>
                    <TableCell>{subscriber.is_active ? 'Actif' : 'Inactif'}</TableCell>
                    <TableCell>{formatDate(subscriber.verified_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-muted-foreground">
                Affichage de {paginatedData.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0} à {Math.min(currentPage * ITEMS_PER_PAGE, sortedData.length)} sur {sortedData.length} abonnés
              </div>
              
              <TablePagination 
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          </>
        </DataLoader>
      </CardContent>
    </Card>
  );
};
